/**
 * PlaylistList Component
 * 
 * Main interface for browsing and managing playlists. This component provides:
 * - Grid view of all playlists with cards
 * - Content type filtering functionality  
 * - Pagination support
 * - Create new playlist functionality
 * - Quick actions menu (edit metadata, manage children, delete)
 * - Navigation to playlist editor and children manager
 * 
 * Used in: App.js route /playlists
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Alert,
    Grid,
    TextField,
    MenuItem,
    Menu,
    MenuItem as MenuItemMui,
    Chip,
    IconButton,
    Pagination
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    PlaylistPlay,
    MoreVert
} from '@mui/icons-material';
import { userRequest } from '../../requestMethods';
import PlaylistMetadataEditor from './PlaylistMetadataEditor';
import PlaylistCard from './PlaylistCard';
import PageContainer from '../layout/PageContainer';

const PlaylistList = () => {
    const navigate = useNavigate();
    const [playlists, setPlaylists] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPlaylist, setCurrentPlaylist] = useState(null);
    const [mode, setMode] = useState('list'); // 'list', 'create', 'edit'
    const [alert, setAlert] = useState({ show: false, message: '', severity: 'info' });
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [contentTypeFilter, setContentTypeFilter] = useState('all');
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedPlaylist, setSelectedPlaylist] = useState(null);

    const contentTypes = [
        { value: 'all', label: 'All Types' },
        { value: 'video', label: 'Video Only' },
        { value: 'text', label: 'Text Only' },
        { value: 'playlist', label: 'Playlists Only' },
        { value: 'mixed', label: 'Mixed Content' }
    ];

    useEffect(() => {
        fetchPlaylists();
    }, [page, contentTypeFilter]);

    const fetchPlaylists = async () => {
        setLoading(true);
        try {
            const response = await userRequest.get('/contentPlaylist', {
                params: {
                    page,
                    limit: 12,
                    contentType: contentTypeFilter
                }
            });
            
            setPlaylists(response.data.playlists || []);
            setTotalPages(response.data.totalPages || 1);
        } catch (error) {
            console.error('Error fetching playlists:', error);
            showAlert('Error fetching playlists', 'error');
        }
        setLoading(false);
    };

    const showAlert = (message, severity = 'info') => {
        setAlert({ show: true, message, severity });
        setTimeout(() => setAlert({ show: false, message: '', severity: 'info' }), 5000);
    };

    const handleCreatePlaylist = () => {
        setCurrentPlaylist(null);
        setMode('create');
    };

    const handleEditPlaylist = (playlist) => {
        setCurrentPlaylist(playlist);
        setMode('edit');
        handleMenuClose();
    };

    const handleManageChildren = (playlist) => {
        navigate(`/playlists/${playlist._id}/children`);
        handleMenuClose();
    };

    const handleCardClick = (playlist) => {
        navigate(`/playlists/${playlist._id}/children`);
    };

    const handleDeletePlaylist = async (playlist) => {
        if (window.confirm(`Are you sure you want to delete "${playlist.title}"? This will not delete the content items within the playlist.`)) {
            try {
                await userRequest.delete(`/contentPlaylist/${playlist._id}`);
                showAlert('Playlist deleted successfully', 'success');
                fetchPlaylists();
            } catch (error) {
                console.error('Error deleting playlist:', error);
                showAlert('Error deleting playlist', 'error');
            }
        }
        handleMenuClose();
    };

    const handleMenuClick = (event, playlist) => {
        setAnchorEl(event.currentTarget);
        setSelectedPlaylist(playlist);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedPlaylist(null);
    };

    const handlePlaylistSaved = () => {
        setMode('list');
        setCurrentPlaylist(null);
        fetchPlaylists();
        showAlert('Playlist saved successfully', 'success');
    };

    const handleCancel = () => {
        setMode('list');
        setCurrentPlaylist(null);
    };

    const getContentTypeColor = (contentType) => {
        const colors = {
            'video': 'error',
            'text': 'primary',
            'playlist': 'secondary',
            'mixed': 'warning'
        };
        return colors[contentType] || 'default';
    };

    if (mode === 'create' || mode === 'edit') {
        return (
            <PlaylistMetadataEditor
                playlist={currentPlaylist}
                onSave={handlePlaylistSaved}
                onCancel={handleCancel}
            />
        );
    }

    return (
        <PageContainer title="Playlists">
            {alert.show && (
                <Alert severity={alert.severity} sx={{ mb: 2 }}>
                    {alert.message}
                </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                        select
                        label="Content Type"
                        value={contentTypeFilter}
                        onChange={(e) => setContentTypeFilter(e.target.value)}
                        size="small"
                        sx={{ minWidth: 150 }}
                    >
                        {contentTypes.map((type) => (
                            <MenuItem key={type.value} value={type.value}>
                                {type.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>

                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleCreatePlaylist}
                >
                    Create Playlist
                </Button>
            </Box>

            {loading ? (
                <Typography>Loading playlists...</Typography>
            ) : playlists.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                        No playlists found
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        Create your first playlist to get started
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleCreatePlaylist}
                    >
                        Create Playlist
                    </Button>
                </Box>
            ) : (
                <>
                    <Grid container spacing={3}>
                        {playlists.map((playlist) => (
                            <Grid item xs={12} sm={6} md={4} key={playlist._id}>
                                <PlaylistCard
                                    playlist={playlist}
                                    onMenuClick={handleMenuClick}
                                    onCardClick={handleCardClick}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Grid container justifyContent="center" sx={{ mt: 3 }}>
                            <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={(event, value) => {
                                        setPage(value);
                                    }}
                                    color="primary"
                                    size="large"
                                    showFirstButton
                                    showLastButton
                                />
                                <Typography variant="body2" color="text.secondary">
                                    Page {page} of {totalPages}
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </>
            )}

            {/* Context Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItemMui onClick={() => handleEditPlaylist(selectedPlaylist)}>
                    <Edit sx={{ mr: 1 }} />
                    Edit Metadata
                </MenuItemMui>
                <MenuItemMui onClick={() => handleManageChildren(selectedPlaylist)}>
                    <PlaylistPlay sx={{ mr: 1 }} />
                    Manage Content
                </MenuItemMui>
                <MenuItemMui onClick={() => handleDeletePlaylist(selectedPlaylist)}>
                    <Delete sx={{ mr: 1 }} />
                    Delete Playlist
                </MenuItemMui>
            </Menu>
        </PageContainer>
    );
};

export default PlaylistList;